import { useEffect, useRef, useState } from 'react'
import { useClickOutside } from '../../hooks/useClickOutside'
import { useIOSScrollLock } from '../../hooks/useIOSScrollLock'
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight'
import { analytics } from '../../utils/analytics'
import { detectPlatform } from '../../utils/browserDetect'
import { AnimatedSpinner } from '../AnimatedSpinner'
import { IQuestionFormProps } from './QuestionForm'
import { SuggestedCompanions } from './SuggestedCompanionsList'
import Suggestions from './Suggestions'

const QuestionFormMobile: React.FC<IQuestionFormProps> = ({
  onChange,
  question,
  clearForm,
  handleAskQuestion,
  isDisabled,
  suggestedCompanions,
  isFetching,
  showSuggestions,
  onFocus,
}) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isInputFocused, setIsInputFocused] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  useVisualViewportHeight()
  useIOSScrollLock(isInputFocused)

  useEffect(() => {
    if (showSuggestions) {
      setIsEditMode(true)
    }
  }, [showSuggestions])

  useEffect(() => {
    const mainContainer = document.getElementById('main-container')
    if (mainContainer) {
      document.body.style.overflow = isEditMode ? 'hidden' : 'auto'
      mainContainer.style.overflow = isEditMode ? 'hidden' : 'auto'
    }
  }, [isEditMode])

  useClickOutside(inputRef, () => {
    inputRef.current?.blur()
  })

  const handleFocus = () => {
    setIsEditMode(true)
    setIsInputFocused(true)
    onFocus()
  }

  const dropEditMode = () => {
    setIsEditMode(false)
  }

  const submitQuery = () => {
    setIsEditMode(false)
    handleAskQuestion()
    analytics.logEvent('watch_session_clicked', {
      source: 'search',
    })
  }

  const onSuggestionClick = (title?: string) => {
    setIsEditMode(false)
    handleAskQuestion(title)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      analytics.logEvent('watch_session_clicked', {
        source: 'search',
      })
      e.preventDefault()
      e.stopPropagation()
      handleAskQuestion()
      setIsEditMode(false)
    }
  }

  const handleBlur = () => {
    setIsInputFocused(false)
  }

  const containerClassName = isEditMode
    ? `padding-0 h-screen-safe ${detectPlatform() === 'android' ? 'top-0' : ''}`
    : 'pb-4 px-4'
  const wrapperClassName = isEditMode
    ? 'h-full p-0 max-w-full rounded-none items-end justify-center pb-2 px-2'
    : 'w-full min-580:w-[70%] rounded-regular p-2 items-center justify-center'

  return (
    <div
      className={`fixed bottom-0 w-full right-0 overflow-hidden z-50 flex items-center justify-center question-form ${containerClassName}`}
    >
      {isEditMode && (
        <>
          <div className="absolute top-1 right-1 p-3 z-10">
            <button
              className="text-white"
              onClick={dropEditMode}
              data-testid="close-question-form-button"
            >
              <i className="fa-solid fa-xmark text-xl" />
            </button>
          </div>
        </>
      )}
      <div
        className={`flex flex-col shadow-md tc-mv-blur gap-2 pointer-events-auto w-full ${wrapperClassName}`}
      >
        {isEditMode && (
          <>
            {!suggestedCompanions && (
              <p className="absolute w-full max-w-[250px] text-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-sm z-0">
                Live suggestions will appear here as you type. Submit for more
                results.
              </p>
            )}
          </>
        )}

        {isEditMode && !!suggestedCompanions?.length && (
          <SuggestedCompanions isMobile companions={suggestedCompanions} />
        )}
        <div className="w-full relative z-50 mt-auto">
          <div className="flex gap-1">
            <div className="relative w-full z-50">
              {!isEditMode && (
                <p className=" text-white text-sm z-0 font-normal mb-1 ms-[2px]">
                  Ask any question or find an expert...
                </p>
              )}
              <div className="relative">
                <input
                  data-testid="question-input-mobile"
                  className="pl-4 pr-8 w-full tc-mv-question-field  h-10 text-white dark:text-white rounded-full"
                  value={question}
                  placeholder="Type anything"
                  onFocus={handleFocus}
                  onChange={onChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  ref={inputRef}
                />
                {question && (
                  <button
                    onClick={clearForm}
                    className="absolute right-3 top-0 h-full flex items-center justify-center text-white opacity-80 hover:opacity-100"
                    data-testid="clear-question-button"
                  >
                    {isFetching ? (
                      <AnimatedSpinner />
                    ) : (
                      <i className="fa-solid fa-xmark text-xl" />
                    )}
                  </button>
                )}
              </div>
            </div>
            {isEditMode && (
              <button
                className="h-10 w-10 min-w-10 flex items-center justify-center text-white font-medium tc-mv-blur align-middle rounded-full border-0 disabled:opacity-50"
                onClick={submitQuery}
                disabled={isDisabled}
                data-testid="submit-question-button"
              >
                <i className="fa-solid fa-arrow-up text-lg" />
              </button>
            )}
          </div>
          <Suggestions onClick={onSuggestionClick} />
        </div>
      </div>
    </div>
  )
}

export { QuestionFormMobile }
